import { computed } from 'vue'
import { useTheme } from './useTheme'
import { parseColor, toCss, mix, contrast, pickOn, isDark, flatten, WHITE, BLACK } from '@/utils/color'

// Token layer on top of useTheme (which is parity-guarded and must not change).
// Everything here is derived from `palette` only.
// The CSS vars are written to document.documentElement from App.vue so that
// teleported UI (modals, sheets) outside #app can read them too.

const { palette } = useTheme()

const SEMANTIC_BASE = {
    success: parseColor('#2e9e6b'),
    danger: parseColor('#d64545'),
    warning: parseColor('#d69e2e')
}

const FALLBACK = {
    primary: parseColor('#0A1A2F'),
    accent: parseColor('#6FA8FF'),
    overlay: parseColor('#B3D4FF'),
    card: parseColor('#FFFFFF'),
    page: parseColor('#F5F9FF'),
    textPrimary: parseColor('#0A1A2F'),
    textStrong: parseColor('#000000'),
    textMuted: parseColor('#7A8CA3')
}

// Parse a palette value; when it is not a colour we can understand (empty string,
// unsupported keyword, gradient…) fall back so derived maths never breaks.
function col(value, fallback) {
    return parseColor(value) || fallback
}

// Raw palette strings pass through unchanged (they may be any CSS colour).
// Empty strings (T3) fall back to the default so the declaration is not dropped.
function raw(value, fallback) {
    return typeof value === 'string' && value.trim() !== '' ? value : fallback
}

let warnedKeys = ''

const tokenState = computed(() => {
    const p = palette.value
    const brand = p.brand || {}
    const text = p.text || {}
    const surface = p.surface || {}
    const border = p.border || {}
    const shadow = p.shadow || {}
    const badge = p.badge || {}
    const hero = p.hero || {}
    const status = p.status || {}
    // Semantic groups (theme decoupling P2). Every leaf defaults to null and a
    // null leaf is DERIVED from the legacy keys below, so an unset key yields
    // exactly the string the legacy token / slice already produced.
    const chrome = p.chrome || {}
    const cta = p.cta || {}
    const announcement = p.announcement || {}

    const primary = col(brand.primary, FALLBACK.primary)
    const accent = col(brand.accent, FALLBACK.accent)
    const overlay = col(brand.overlay, FALLBACK.overlay)
    const card = col(surface.card, FALLBACK.card)
    const page = col(surface.page, FALLBACK.page)
    const textPrimary = col(text.primary, FALLBACK.textPrimary)
    const textStrong = col(text.strong, FALLBACK.textStrong)
    const textMuted = col(text.muted, FALLBACK.textMuted)

    const dark = isDark(page)
    const gradientMid = mix(overlay, accent, 0.5)

    // The focus ring must be visible against the page. brand.accent equals
    // surface.page on some tenants (the demo tenant is dark teal for both),
    // which would make the ring invisible, so pick the best-contrasting brand
    // colour and fall back to plain white/black.
    // Keep it on-brand when a brand colour is clearly visible (>= 3:1), else
    // fall back to whichever candidate contrasts most.
    const focusCandidates = [accent, primary, col(brand.secondary, primary)]
    const focusColor = focusCandidates.find((c) => contrast(c, page) >= 3) ||
        pickOn(page, [...focusCandidates, WHITE, BLACK])

    // BLACK/WHITE are always candidates: a tenant can set text.strong to the same
    // value as brand.primary (the demo tenant does), which would otherwise leave
    // only white to pick from and land at ~2.4:1 on a light brand colour.
    const onPrimary = pickOn(primary, [WHITE, BLACK, textStrong])
    const onAccent = pickOn(gradientMid, [WHITE, BLACK, textStrong, textPrimary])

    const semantic = {}
    const semanticSoft = {}
    const onSemantic = {}
    for (const key of Object.keys(SEMANTIC_BASE)) {
        semantic[key] = mix(SEMANTIC_BASE[key], accent, 0.30)
        semanticSoft[key] = mix(card, semantic[key], 0.14)
        onSemantic[key] = pickOn(semantic[key], [WHITE, BLACK, textStrong])
    }

    // Contrast safety: text.primary is what today's gradient buttons use.
    const failing = []
    const gradientContrast = contrast(textPrimary, gradientMid)
    const textOnGradient = gradientContrast >= 4.5 ? textPrimary : onAccent
    if (gradientContrast < 4.5) failing.push('text.primary on brand.overlay→brand.accent')

    const shadowOf = (value, fallback) => `0 4px 12px ${raw(value, fallback)}`
    // Optional tenant background images (header / footer / sheet summary). The
    // legacy slices emitted `null` for an unset or empty value and the component
    // turned that into the CSS keyword `none`; the token does both in one step,
    // so the declaration is always present and always valid.
    const bgImage = (value) => (typeof value === 'string' && value.trim() !== '' ? `url(${value})` : 'none')
    // "Explicitly set" is the same test raw() applies: a null / '' leaf is derived.
    const isSet = (value) => typeof value === 'string' && value.trim() !== ''
    const rawPrimary = raw(brand.primary, toCss(primary))
    const rawAccent = raw(brand.accent, toCss(accent))
    const rawOverlay = raw(brand.overlay, toCss(overlay))
    const rawTextPrimary = raw(text.primary, toCss(textPrimary))
    const backdrop = dark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.6)'
    // The #app page gradient (theme decoupling P7, was the globalBackground slice).
    const rawGradientMid = raw(surface.gradientMid, raw(surface.page, toCss(page)))
    const rawGradientBottom = raw(surface.gradientBottom, rawGradientMid)

    // --- chrome: the header / footer bars -------------------------------------
    // Stops default to the headerTheme / footerTheme slice (overlay -> page ->
    // accent). Link and body text default to text.primary / text.secondary but
    // must read on ALL THREE stops; when they do not, swap to a candidate that
    // reads on all three, or failing that the one with the best worst-case.
    const chromeStart = col(chrome.start, overlay)
    const chromeMid = col(chrome.mid, page)
    const chromeEnd = col(chrome.end, accent)
    // Stops are frequently semi-transparent (the demo tenant's overlay is 50%
    // alpha over a dark teal page). contrast() composites an alpha background
    // over WHITE, which would read those stops as light and swap the link to
    // black on a dark bar. Composite them over what is actually behind instead:
    // the #app gradient (surface.gradientMid at the top, where both bars sit),
    // itself flattened over the browser canvas. Only the CONTRAST MATHS uses
    // these; --gradient-chrome still emits the raw strings, so paint is unchanged.
    const appBase = flatten(col(surface.gradientMid, page), WHITE)
    const chromeStops = [chromeStart, chromeMid, chromeEnd].map((s) => flatten(s, appBase))
    const readsOnChrome = (c) => chromeStops.every((s) => contrast(c, s) >= 4.5)
    const worstChromeStop = (c) => chromeStops.reduce((worst, s) => (contrast(c, s) < contrast(c, worst) ? s : worst))
    const worstOnChrome = (c) => Math.min(...chromeStops.map((s) => contrast(c, s)))
    // pickOn() answers "what reads on THIS colour", so aiming it at the worst stop
    // can return a colour that then fails on one of the other two — a bar whose
    // stops span lightness (dark / light / dark) gets text picked for the dark end
    // and painted across the light middle. Keep that answer when it does read on
    // all three; otherwise take the candidate with the best worst-case, so the
    // swap degrades to "as legible as this bar allows" instead of silently failing.
    const CHROME_CANDIDATES = [WHITE, BLACK, textStrong, textPrimary]
    const onChrome = (c) => {
        const first = pickOn(worstChromeStop(c), CHROME_CANDIDATES)
        if (readsOnChrome(first)) return first
        return CHROME_CANDIDATES.reduce((best, cand) => (worstOnChrome(cand) > worstOnChrome(best) ? cand : best))
    }
    const textSecondary = col(text.secondary, textPrimary)
    const rawTextSecondary = raw(text.secondary, rawTextPrimary)
    const chromeLink = raw(chrome.link, readsOnChrome(textPrimary) ? rawTextPrimary : toCss(onChrome(textPrimary)))
    const chromeText = raw(chrome.text, readsOnChrome(textSecondary) ? rawTextSecondary : toCss(onChrome(textSecondary)))
    if (isSet(chrome.link) && !readsOnChrome(col(chrome.link, textPrimary))) failing.push('chrome.link on chrome.start→chrome.end')
    if (isSet(chrome.text) && !readsOnChrome(col(chrome.text, textSecondary))) failing.push('chrome.text on chrome.start→chrome.end')

    // --- cta: solid button (bg/fg) and gradient button (start/end/text) --------
    // Solid defaults to brand.primary with the --color-on-primary pick; gradient
    // defaults to the --gradient-brand stops. Unlike --text-on-gradient (which
    // tests the midpoint) the gradient text must read on BOTH stops.
    const ctaBg = col(cta.bg, primary)
    const onCta = pickOn(ctaBg, [WHITE, BLACK, textStrong])
    // Same alpha problem as the chrome stops: gradient CTAs sit on the card
    // (sheet, FAQ panel) or, in the hero, on the page — both over the #app
    // gradient. Composite before measuring; the emitted stops stay raw.
    const ctaBase = flatten(card, appBase)
    const ctaStart = flatten(col(cta.gradientStart, overlay), ctaBase)
    const ctaEnd = flatten(col(cta.gradientEnd, accent), ctaBase)
    const rawCtaStart = raw(cta.gradientStart, rawOverlay)
    const rawCtaEnd = raw(cta.gradientEnd, rawAccent)
    const readsOnCta = (c) => contrast(c, ctaStart) >= 4.5 && contrast(c, ctaEnd) >= 4.5
    const onCtaGradient = pickOn(mix(ctaStart, ctaEnd, 0.5), [WHITE, BLACK, textStrong, textPrimary])
    const textOnCta = raw(cta.gradientText, readsOnCta(textPrimary) ? toCss(textPrimary) : toCss(onCtaGradient))
    if (isSet(cta.fg) && contrast(col(cta.fg, onCta), ctaBg) < 4.5) failing.push('cta.fg on cta.bg')
    if (isSet(cta.gradientText) && !readsOnCta(col(cta.gradientText, textPrimary))) failing.push('cta.gradientText on cta.gradientStart→cta.gradientEnd')

    // --- status: override-aware semantic colours --------------------------------
    // status.tint replaces brand.accent as the 30% tint; status.success /
    // danger / warning replace the mixed colour outright. --color-success etc.
    // above keep the legacy (accent-tinted) computation until P3 moves consumers.
    const statusTint = col(status.tint, accent)
    const statusColor = {}
    const statusSoft = {}
    const onStatus = {}
    for (const key of Object.keys(SEMANTIC_BASE)) {
        statusColor[key] = col(status[key], mix(SEMANTIC_BASE[key], statusTint, 0.30))
        statusSoft[key] = mix(card, statusColor[key], 0.14)
        onStatus[key] = pickOn(statusColor[key], [WHITE, BLACK, textStrong])
    }

    // --- announcement: the modal's Close / Previous / Next buttons -------------
    // The announcement modal is deliberately palette-independent by default. It is
    // rare and carries operational messages (closures, delays, price changes), so
    // legibility beats brand expression. Painting its buttons in brand colours put
    // the label at 1.5-3:1 on nine of eleven reference palettes, because the brand
    // gradient runs light-to-dark and no flat label colour reads on both ends.
    // A tenant that wants brand colours sets announcement.start / end / text.
    // (The modal's header band is --gradient-brand, not these tokens.)
    const ANNOUNCEMENT_SURFACE = '#FFFFFF'
    const rawAnnouncementStart = raw(announcement.start, ANNOUNCEMENT_SURFACE)
    const rawAnnouncementEnd = raw(announcement.end, ANNOUNCEMENT_SURFACE)
    // AnnouncementModal.vue paints its body, nav bar and footer in fixed colours
    // (white body, paper bars, dark ink), so the buttons sit on that paper tone on
    // every palette, never on surface.card. Composite their stops over it. Keep
    // ANNOUNCEMENT_FOOTER in step with --ann-paper in AnnouncementModal.vue.
    const ANNOUNCEMENT_FOOTER = parseColor('#F7F6F3')
    const announcementStart = flatten(col(announcement.start, WHITE), ANNOUNCEMENT_FOOTER)
    const announcementEnd = flatten(col(announcement.end, WHITE), ANNOUNCEMENT_FOOTER)
    const readsOnAnnouncement = (c) => contrast(c, announcementStart) >= 4.5 && contrast(c, announcementEnd) >= 4.5
    // Keep the tenant's own ink when it reads on the surface (a light palette's
    // dark ink does), else swap. On the white default this always resolves dark.
    // A tenant who sets the stops to brand colours gets the same swap the CTA has.
    const onAnnouncement = pickOn(mix(announcementStart, announcementEnd, 0.5), [WHITE, BLACK, textStrong, textPrimary])
    const announcementText = raw(announcement.text, readsOnAnnouncement(textPrimary) ? rawTextPrimary : toCss(onAnnouncement))
    // A white button on that paper footer has almost no edge (1.08:1), and
    // border.main is a palette-tinted card hairline that does not help. Derive the
    // edge from the button's own ink instead, so it is visible on every palette.
    const announcementInk = isSet(announcement.text)
        ? col(announcement.text, textPrimary)
        : (readsOnAnnouncement(textPrimary) ? textPrimary : onAnnouncement)
    const announcementBorder = toCss(mix(announcementStart, announcementInk, 0.55))
    if (isSet(announcement.text) && !readsOnAnnouncement(col(announcement.text, textPrimary))) failing.push('announcement.text on announcement.start→announcement.end')

    // --- text on cards ------------------------------------------------------------
    if (isSet(text.onCard) && contrast(col(text.onCard, textPrimary), card) < 4.5) failing.push('text.onCard on surface.card')
    if (isSet(text.link) && contrast(col(text.link, primary), card) < 4.5) failing.push('text.link on surface.card')

    if (process.env.NODE_ENV !== 'production' && failing.length) {
        const sig = failing.join('|')
        if (sig !== warnedKeys) {
            warnedKeys = sig
            console.warn('[ThemeTokens] contrast below 4.5:1 for', failing)
        }
    }

    const cssVars = {
        // Direct mappings
        '--color-primary': raw(brand.primary, toCss(primary)),
        '--color-secondary': raw(brand.secondary, raw(brand.primary, toCss(primary))),
        '--color-accent': raw(brand.accent, toCss(accent)),
        '--gradient-brand': `linear-gradient(135deg, ${raw(brand.overlay, toCss(overlay))}, ${raw(brand.accent, toCss(accent))})`,

        '--text-primary': raw(text.primary, toCss(textPrimary)),
        '--text-secondary': raw(text.secondary, raw(text.primary, toCss(textPrimary))),
        '--text-muted': raw(text.muted, toCss(textMuted)),
        '--text-strong': raw(text.strong, toCss(textStrong)),
        // The hero motto. Was globalTitle.secondaryTitleColor (P7).
        '--text-subtitle': raw(text.subtitle, raw(text.primary, toCss(textPrimary))),

        '--surface-page': raw(surface.page, toCss(page)),
        '--surface-card': raw(surface.card, toCss(card)),
        '--surface-input': raw(surface.input, toCss(card)),
        '--surface-hover': raw(surface.hover, toCss(mix(card, primary, 0.08))),
        // #app's own background, and its top stop on its own — HeroSection fades
        // into a COLOUR, which a gradient string cannot be. Was globalBackground
        // + an inline palette read in App.vue (P7).
        '--page-background': `linear-gradient(180deg, ${rawGradientMid} 0%, ${rawGradientMid} 50%, ${rawGradientBottom} 100%)`,
        '--page-top': rawGradientMid,

        '--border-main': raw(border.main, toCss(mix(card, textMuted, 0.4))),
        '--border-light': raw(border.light, toCss(mix(card, textMuted, 0.25))),
        '--border-featured': raw(border.featured, toCss(accent)),

        '--shadow-brand': shadowOf(shadow.brand, 'rgba(0, 0, 0, 0.2)'),
        '--shadow-strong': shadowOf(shadow.brandStrong, 'rgba(0, 0, 0, 0.3)'),
        '--shadow-secondary': shadowOf(shadow.secondary, 'rgba(0, 0, 0, 0.3)'),
        // The atmosphere tiles' glow. Deliberately NOT shadowOf(): the
        // atmosphereTheme slice blurred it 16px, not 12px (P7).
        '--shadow-glow': `0 4px 16px ${raw(shadow.brandGlow, 'rgba(0, 0, 0, 0.25)')}`,
        // Colour only, for the rules that compose their own offset: FaqSection's
        // panel (0 4px 6px) and its hover (0 8px 12px). Same idea as
        // --chrome-shadow-color below. Was offerTheme.shadowColor /
        // offerTheme.hoverShadowColor (P7).
        '--shadow-brand-color': raw(shadow.brand, 'rgba(0, 0, 0, 0.2)'),
        '--shadow-secondary-color': raw(shadow.secondary, 'rgba(0, 0, 0, 0.3)'),
        // Colour only: every section title composes its own offset, and they are
        // not all the same (the hero h1 is 2px 2px 4px, everything else
        // 1px 1px 3px), so a composed token could not serve them all.
        // Was globalTitle.textShadow (P7).
        '--text-shadow-color': raw(shadow.dark, 'rgba(0, 0, 0, 0.2)'),

        '--badge-bg': raw(badge.background, toCss(primary)),
        '--badge-text': raw(badge.text, toCss(onPrimary)),

        '--hero-overlay-start': raw(hero.overlayStart, 'rgba(255, 255, 255, 0.3)'),
        '--hero-overlay-end': raw(hero.overlayEnd, 'rgba(255, 255, 255, 0.5)'),

        '--color-closed': raw(status.closed, toCss(textMuted)),

        // Optional tenant artwork layered over the chrome / summary gradients.
        // `none` when unset, so the declaration is always valid CSS. Was
        // headerTheme / footerTheme / priceCardTheme backgroundImage (P7).
        '--bg-image-header': bgImage(brand.backgroundHeader),
        '--bg-image-footer': bgImage(brand.backgroundFooter),
        '--bg-image-summary': bgImage(brand.backgroundPriceSummary),

        // Derived
        '--color-on-primary': toCss(onPrimary),
        '--color-on-accent': toCss(onAccent),
        '--color-primary-soft': toCss(mix(card, primary, 0.12)),
        '--color-accent-soft': toCss(mix(card, accent, 0.16)),
        '--surface-elevated': toCss(mix(card, WHITE, dark ? 0.06 : 0.10)),
        '--surface-muted': toCss(mix(card, textMuted, 0.20)),

        '--color-success': toCss(semantic.success),
        '--color-danger': toCss(semantic.danger),
        '--color-warning': toCss(semantic.warning),
        '--color-success-soft': toCss(semanticSoft.success),
        '--color-danger-soft': toCss(semanticSoft.danger),
        '--color-warning-soft': toCss(semanticSoft.warning),
        '--color-on-danger': toCss(onSemantic.danger),

        '--overlay-backdrop': backdrop,
        '--focus-ring': `0 0 0 2px ${toCss(page)}, 0 0 0 4px ${toCss(focusColor)}`,
        '--scheme': dark ? 'dark' : 'light',

        '--text-on-gradient': toCss(textOnGradient),

        // Semantic (theme decoupling P2). Each is `<semantic key> ?? <legacy
        // derivation>`; with every semantic key null these reproduce the legacy
        // strings above / the slices in useTheme.js byte for byte.
        // Same 135deg + 0/50/100% stops as Header.vue / Footer.vue.
        '--gradient-chrome': `linear-gradient(135deg, ${raw(chrome.start, rawOverlay)} 0%, ${raw(chrome.mid, raw(surface.page, toCss(page)))} 50%, ${raw(chrome.end, rawAccent)} 100%)`,
        '--chrome-link': chromeLink,
        '--chrome-link-hover': raw(chrome.linkHover, rawPrimary),
        '--chrome-text': chromeText,
        // Colour only: Header.vue / Footer.vue compose their own offsets
        // (0 4px 12px downward / 0 -4px 12px upward) from it. The composed
        // `--chrome-shadow` that used to sit here lost its last consumer in P3a
        // and was removed in P7.
        '--chrome-shadow-color': raw(chrome.shadow, raw(shadow.dark, 'rgba(0, 0, 0, 0.2)')),

        '--cta-bg': raw(cta.bg, rawPrimary),
        '--cta-fg': raw(cta.fg, toCss(onCta)),
        '--gradient-cta': `linear-gradient(135deg, ${rawCtaStart}, ${rawCtaEnd})`,
        // The stops on their own, for the one place a stop is a solid colour
        // (FaqSection's inverted CTA button paints its text with the start stop).
        '--cta-gradient-start': rawCtaStart,
        '--cta-gradient-end': rawCtaEnd,
        '--text-on-cta': textOnCta,

        '--color-heading': raw(text.heading, rawPrimary),
        '--color-link': raw(text.link, rawPrimary),
        '--text-on-card': raw(text.onCard, raw(text.onBrand, rawTextPrimary)),

        '--status-success': raw(status.success, toCss(statusColor.success)),
        '--status-danger': raw(status.danger, toCss(statusColor.danger)),
        '--status-warning': raw(status.warning, toCss(statusColor.warning)),
        '--status-success-soft': toCss(statusSoft.success),
        '--status-danger-soft': toCss(statusSoft.danger),
        '--status-warning-soft': toCss(statusSoft.warning),
        '--on-status-success': toCss(onStatus.success),
        '--on-status-danger': toCss(onStatus.danger),
        '--on-status-warning': toCss(onStatus.warning),

        '--announcement-start': rawAnnouncementStart,
        '--announcement-end': rawAnnouncementEnd,
        '--announcement-text': announcementText,
        '--announcement-border': announcementBorder,
        // Same 135deg two-stop form as AnnouncementModal.vue's band / close button.
        '--gradient-announcement': `linear-gradient(135deg, ${rawAnnouncementStart}, ${rawAnnouncementEnd})`,

        '--surface-backdrop': raw(surface.backdrop, backdrop)
    }

    // camelCase mirror for JS consumers (e.g. Stripe Elements appearance API)
    const tokens = {}
    for (const [k, v] of Object.entries(cssVars)) {
        const camel = k.slice(2).replace(/-([a-z0-9])/g, (_, ch) => ch.toUpperCase())
        tokens[camel] = v
    }

    return { cssVars, tokens }
})

const cssVars = computed(() => tokenState.value.cssVars)
const tokens = computed(() => tokenState.value.tokens)

export const useThemeTokens = () => ({ cssVars, tokens })
